"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useAppBuildId } from "@/app/providers/AppBuildIdProvider/AppBuildIdProvider";
export const APP_VERSION_POLL_INTERVAL_MS = 60_000;
interface VersionResponse {
  version: string | null;
}
interface AppVersionCheck {
  isStale: boolean;
  currentVersion?: string;
  latestVersion?: string;
}
async function fetchServerVersion(): Promise<VersionResponse> {
  const response = await fetch("/api/version", {
    cache: "no-store"
  });
  if (!response.ok) throw new Error(`Version check failed with ${response.status}`);
  return response.json();
}
function isNewer(current: string | undefined, latest: string | null | undefined): boolean {
  return Boolean(current && latest && latest !== current);
}

/**
 * Polls the server's build id and reports when it differs from the build that rendered this
 * page. Once stale the query is disabled outright: the answer cannot change back, the next
 * navigation hard-reloads anyway, and a disabled query is skipped by the mutation-driven
 * invalidation in getQueryClient.
 */
export function useAppVersionCheck() {
  const $ = _c(10);
  const currentVersion = useAppBuildId() ?? undefined;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["app-version"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== currentVersion) {
    t1 = {
      queryKey: t0,
      queryFn: fetchServerVersion,
      staleTime: 0,
      enabled: query => Boolean(currentVersion) && !isNewer(currentVersion, query.state.data?.version),
      refetchInterval: query_0 => isNewer(currentVersion, query_0.state.data?.version) ? false : APP_VERSION_POLL_INTERVAL_MS,
      refetchOnWindowFocus: query_1 => !isNewer(currentVersion, query_1.state.data?.version)
    };
    $[1] = currentVersion;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data
  } = useQuery(t1);
  const t2 = data?.version;
  let t3;
  if ($[3] !== currentVersion || $[4] !== t2) {
    t3 = isNewer(currentVersion, t2);
    $[3] = currentVersion;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = data?.version ?? undefined;
  let t5;
  if ($[6] !== currentVersion || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      isStale: t3,
      currentVersion,
      latestVersion: t4
    };
    $[6] = currentVersion;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}